import { QuoteCardType } from 'components/QuoteCard';
import { Maybe, Undef } from 'types/UtilityTypes';
import { Quoting } from 'types/enums/Integrations';

import { SelectedQuoteType } from '../types';

// Not a Quoting member: TMS lane history is a QuoteSource without a pricing
// integration, matching how q.source is compared elsewhere (see QuickQuoteForm).
export const TMS_LANE_HISTORY_SOURCE = 'tms_lane_history';

const LANE_HISTORY_CARD_TYPES = new Set<SelectedQuoteType>([
  SelectedQuoteType.LANE_HISTORY,
  SelectedQuoteType.MCLEOD_LANE_HISTORY,
  SelectedQuoteType.TURVO_LANE_HISTORY,
  SelectedQuoteType.GLOBALTRANZ_LANE_HISTORY,
  SelectedQuoteType.GLOBALTRANZ_TMS_LANE_HISTORY,
  SelectedQuoteType.OLYMPUS_DAT_LANE_HISTORY,
  SelectedQuoteType.ALJEX_LANE_HISTORY,
]);

const DAT_CARD_TYPES = new Set<SelectedQuoteType>([
  SelectedQuoteType.DAT_RATEVIEW,
  SelectedQuoteType.DAT_LONGEST_LEG,
  SelectedQuoteType.DAT_LEG_TO_LEG,
]);

const findLaneHistoryCard = (cards: QuoteCardType[]) =>
  cards.find(
    (card) =>
      LANE_HISTORY_CARD_TYPES.has(card.type) ||
      String(card.type).toLowerCase().includes('lane_history')
  );

const findDatCard = (cards: QuoteCardType[]) =>
  cards.find((card) => DAT_CARD_TYPES.has(card.type));

// Finds the first card matching the given types, in priority order.
const firstOfTypes =
  (...types: SelectedQuoteType[]) =>
  (cards: QuoteCardType[]): Undef<QuoteCardType> => {
    for (const type of types) {
      const match = cards.find((card) => card.type === type);
      if (match) {
        return match;
      }
    }
    return undefined;
  };

// Maps a quote source to the card it should select. Any other configured value
// is matched directly against a SelectedQuoteType (e.g. "gs_network"), so the
// config can target a whole source or one specific card.
const SOURCE_CARD_MATCHERS: Record<
  string,
  (cards: QuoteCardType[]) => Undef<QuoteCardType>
> = {
  [Quoting.Greenscreens]: firstOfTypes(
    SelectedQuoteType.GS_BUYPOWER,
    SelectedQuoteType.GS_NETWORK
  ),
  [Quoting.DAT]: findDatCard,
  [Quoting.TruckStop]: firstOfTypes(
    SelectedQuoteType.TRUCKSTOP_BOOKED,
    SelectedQuoteType.TRUCKSTOP_POSTED
  ),
  [Quoting.Sonar]: firstOfTypes(SelectedQuoteType.SONAR),
  [Quoting.Transfix]: firstOfTypes(SelectedQuoteType.TRANSFIX),
  [Quoting.SuperDispatch]: firstOfTypes(SelectedQuoteType.SUPER_DISPATCH),
  [Quoting.KencoFlash]: firstOfTypes(SelectedQuoteType.KENCO_FLASH_AVERAGE),
  [Quoting.TruPay]: firstOfTypes(SelectedQuoteType.TRUPAY),
  [Quoting.GlobalTranz]: firstOfTypes(
    SelectedQuoteType.GLOBALTRANZ_LANE_HISTORY,
    SelectedQuoteType.GLOBALTRANZ_TMS_LANE_HISTORY
  ),
  [TMS_LANE_HISTORY_SOURCE]: findLaneHistoryCard,
};

const findConfiguredCard = (cards: QuoteCardType[], source: string) =>
  SOURCE_CARD_MATCHERS[source]?.(cards) ??
  cards.find((card) => String(card.type).toLowerCase() === source);

export type SelectDefaultQuoteCardInput = {
  quoteCards: QuoteCardType[];
  defaultFTLBuyRateSource?: Maybe<string>;
  isTMSLaneHistoryEnabled: boolean;
  hasLaneHistoryResults: boolean;
};

export function selectDefaultQuoteCard({
  quoteCards,
  defaultFTLBuyRateSource,
  isTMSLaneHistoryEnabled,
  hasLaneHistoryResults,
}: SelectDefaultQuoteCardInput): Undef<QuoteCardType> {
  if (!quoteCards.length) {
    return undefined;
  }

  // A configured source wins over every other rule (including Kenco); when it
  // matches no card we fall through to the legacy logic below.
  const configuredSource = defaultFTLBuyRateSource?.trim().toLowerCase();
  if (configuredSource) {
    const configuredCard = findConfiguredCard(quoteCards, configuredSource);
    if (configuredCard) {
      return configuredCard;
    }
  }

  const kencoCard = quoteCards.find(
    (card) => card.type === SelectedQuoteType.KENCO_FLASH_AVERAGE
  );
  if (kencoCard) {
    return kencoCard;
  }

  if (!configuredSource && isTMSLaneHistoryEnabled && hasLaneHistoryResults) {
    const laneHistoryCard = findLaneHistoryCard(quoteCards);
    if (laneHistoryCard) {
      return laneHistoryCard;
    }
  }

  return (
    findDatCard(quoteCards) ??
    quoteCards.reduce((prev, curr) => (prev.cost < curr.cost ? prev : curr))
  );
}
